import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../../lib/api';
const API_URL = API_BASE;
import { CheckCircle, XCircle, RefreshCw, Filter, Calendar } from 'lucide-react';
import { erpToast } from '../../components/erp/ERPToast';

const hdrs = () => {
  const t = localStorage.getItem('retailcliq_token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
};

const STATUS_BADGE = {
  Pending:   { bg: '#fef3c7', color: '#92400e', label: 'Pending' },
  Deposited: { bg: '#dbeafe', color: '#1d4ed8', label: 'Deposited' },
  Cleared:   { bg: '#d1fae5', color: '#065f46', label: 'Cleared' },
  Bounced:   { bg: '#fee2e2', color: '#991b1b', label: 'Bounced' },
  Cancelled: { bg: '#f3f4f6', color: '#6b7280', label: 'Cancelled' },
};

const PDCReconciliationScreen = ({ theme = 'light' }) => {
  const [pdcType, setPdcType]       = useState('receipt');
  const [statusFilter, setFilter]   = useState('all');
  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [selected, setSelected]     = useState(null);   // row being reconciled
  const [reconStatus, setReconStatus] = useState('Cleared');
  const [reconDate, setReconDate]   = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving]         = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_URL}/api/vouchers/pdc-adjustments?type=${pdcType}`;
      const r = await fetch(url, { headers: hdrs() });
      const j = await r.json();
      if (j.success) setRows(j.data || []);
    } catch (e) {
      erpToast.error('Failed to load PDC vouchers');
    }
    setLoading(false);
  }, [pdcType]);

  useEffect(() => { load(); }, [load]);

  const filteredRows = statusFilter === 'all'
    ? rows
    : rows.filter(r => r.PDCStatus === statusFilter);

  const pendingCount = rows.filter(r => r.PDCStatus === 'Pending' || r.PDCStatus === 'Deposited').length;

  const handleReconcile = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/vouchers/pdc-adjustments/${selected.DocId}`, {
        method: 'PUT',
        headers: hdrs(),
        body: JSON.stringify({ pdcStatus: reconStatus, chequeDate: reconDate, type: pdcType }),
      });
      const j = await r.json();
      if (j.success) {
        erpToast.success(`PDC ${selected.DocId} marked as ${reconStatus}`);
        setSelected(null);
        await load();
      } else {
        erpToast.error(j.error || 'Update failed');
      }
    } catch (e) {
      erpToast.error('Save failed: ' + e.message);
    }
    setSaving(false);
  };

  const fmt = v => (v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="erp-module" data-testid="pdc-reconciliation-screen" data-dev-component="PDCReconciliationScreen">
      {/* Header */}
      <div className="erp-screen-header">
        <span className="erp-screen-title">PDC Reconciliation</span>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, marginLeft: 16 }}>
          <select
            value={pdcType}
            onChange={e => { setPdcType(e.target.value); setSelected(null); }}
            className="erp-input erp-select"
            style={{ width: 160, height: 26, fontSize: 12 }}
            data-testid="pdc-recon-type-select"
          >
            <option value="receipt">PDC Receipts</option>
            <option value="payment">PDC Payments</option>
          </select>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Filter size={12} style={{ color: 'var(--erp-text-muted)' }} />
            <select
              value={statusFilter}
              onChange={e => setFilter(e.target.value)}
              className="erp-input erp-select"
              style={{ width: 130, height: 26, fontSize: 12 }}
              data-testid="pdc-recon-status-filter"
            >
              <option value="all">All Statuses</option>
              <option value="Pending">Pending</option>
              <option value="Deposited">Deposited</option>
              <option value="Cleared">Cleared</option>
              <option value="Bounced">Bounced</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
          {pendingCount > 0 && (
            <span style={{ fontSize: 11, background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>
              {pendingCount} pending reconciliation
            </span>
          )}
        </div>
        <div className="erp-screen-actions">
          <button className="erp-btn erp-btn-ghost" onClick={load} data-testid="pdc-recon-refresh-btn">
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--erp-sp-3)' }}>
        {/* Reconciliation Panel — shown when a row is selected */}
        {selected && (
          <div
            className="erp-form-card"
            style={{ marginBottom: 12, borderLeft: '3px solid var(--erp-primary)', padding: '12px 16px' }}
            data-testid="pdc-recon-panel"
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--erp-primary)', marginBottom: 8 }}>
              Reconcile PDC Voucher: {selected.DocId}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px 20px', marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--erp-text-muted)', fontWeight: 600 }}>Account</div>
                <div style={{ fontSize: 12 }}>{selected.AccountName || selected.ACId}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--erp-text-muted)', fontWeight: 600 }}>Cheque No</div>
                <div style={{ fontSize: 12, fontFamily: 'var(--erp-font-mono)' }}>{selected.Chequeno || '-'}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--erp-text-muted)', fontWeight: 600 }}>Amount</div>
                <div style={{ fontSize: 12, fontFamily: 'var(--erp-font-mono)', fontWeight: 700 }}>{fmt(selected.Amount)}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--erp-text-muted)', fontWeight: 600 }}>Current Status</div>
                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: STATUS_BADGE[selected.PDCStatus]?.bg, color: STATUS_BADGE[selected.PDCStatus]?.color, fontWeight: 700 }}>
                  {selected.PDCStatus}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end' }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--erp-text-muted)', fontWeight: 600, marginBottom: 3 }}>Mark as</div>
                <select
                  value={reconStatus}
                  onChange={e => setReconStatus(e.target.value)}
                  className="erp-input erp-select"
                  style={{ width: 140, fontSize: 12 }}
                  data-testid="pdc-recon-new-status"
                >
                  <option value="Cleared">Cleared</option>
                  <option value="Bounced">Bounced</option>
                  <option value="Deposited">Deposited</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--erp-text-muted)', fontWeight: 600, marginBottom: 3 }}>
                  <Calendar size={10} style={{ display: 'inline', marginRight: 4 }} />
                  {reconStatus === 'Cleared' ? 'Clearance Date' : reconStatus === 'Bounced' ? 'Bounce Date' : 'Effective Date'}
                </div>
                <input
                  type="date"
                  value={reconDate}
                  onChange={e => setReconDate(e.target.value)}
                  className="erp-input"
                  style={{ width: 160, fontSize: 12 }}
                  data-testid="pdc-recon-date"
                />
              </div>
              <button
                className="erp-btn erp-btn-primary"
                onClick={handleReconcile}
                disabled={saving}
                style={{ height: 30, fontSize: 12 }}
                data-testid="pdc-recon-save-btn"
              >
                <CheckCircle size={13} /> {saving ? 'Saving...' : 'Confirm Reconciliation'}
              </button>
              <button
                className="erp-btn erp-btn-ghost"
                onClick={() => setSelected(null)}
                style={{ height: 30, fontSize: 12 }}
                data-testid="pdc-recon-cancel-btn"
              >
                <XCircle size={13} /> Cancel
              </button>
            </div>
          </div>
        )}

        {/* PDC List */}
        <div className="erp-form-card">
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--erp-text-muted)', fontSize: 13 }}>
              Loading PDC vouchers...
            </div>
          ) : (
            <div style={{ overflow: 'auto' }}>
              <table className="erp-grid" data-testid="pdc-recon-grid">
                <thead>
                  <tr>
                    <th style={{ width: 110 }}>Doc ID</th>
                    <th style={{ width: 110 }}>Doc Date</th>
                    <th style={{ minWidth: 160 }}>Account Name</th>
                    <th style={{ width: 120 }}>Cheque No</th>
                    <th style={{ width: 110 }}>Cheque Date</th>
                    <th style={{ width: 110, textAlign: 'right' }}>Amount</th>
                    <th style={{ width: 110, textAlign: 'center' }}>PDC Status</th>
                    <th style={{ width: 90, textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ padding: 32, textAlign: 'center', color: 'var(--erp-text-muted)', fontSize: 12 }}>
                        No PDC vouchers found for the selected filter.
                      </td>
                    </tr>
                  ) : filteredRows.map((row, i) => {
                    const badge = STATUS_BADGE[row.PDCStatus] || STATUS_BADGE.Pending;
                    const canReconcile = row.PDCStatus === 'Pending' || row.PDCStatus === 'Deposited';
                    const isActive = selected?.DocId === row.DocId;
                    return (
                      <tr
                        key={i}
                        data-testid={`pdc-recon-row-${i}`}
                        style={isActive ? { background: 'var(--erp-primary-light, #eff6ff)' } : undefined}
                      >
                        <td style={{ fontFamily: 'var(--erp-font-mono)', fontSize: 11 }}>{row.DocId}</td>
                        <td style={{ fontSize: 11 }}>{row.DocDateFormatted || row.DocDate || '-'}</td>
                        <td style={{ fontSize: 12 }}>{row.AccountName || row.ACId}</td>
                        <td style={{ fontFamily: 'var(--erp-font-mono)', fontSize: 11 }}>{row.Chequeno || '-'}</td>
                        <td style={{ fontSize: 11 }}>{row.ChequeDate || '-'}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12, fontWeight: 600 }}>{fmt(row.Amount)}</td>
                        <td style={{ textAlign: 'center' }}>
                          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: badge.bg, color: badge.color, fontWeight: 700 }}>
                            {badge.label}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {canReconcile ? (
                            <button
                              className="erp-btn erp-btn-ghost"
                              style={{ fontSize: 11, padding: '2px 8px', color: isActive ? 'var(--erp-primary)' : undefined }}
                              onClick={() => { setSelected(row); setReconDate(new Date().toISOString().split('T')[0]); setReconStatus('Cleared'); }}
                              data-testid={`pdc-recon-action-${i}`}
                            >
                              Reconcile
                            </button>
                          ) : (
                            <span style={{ fontSize: 11, color: 'var(--erp-text-muted)' }}>-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PDCReconciliationScreen;
